import { familyRelationships } from '@data/familyRelationship';
import {
  BeneficiaryT,
  CheckoutT,
  DependantsInformationT,
  IdentityValidationT,
  InsuranceT,
  LeadDbT,
  LeadT,
} from '@segurodeceso/core/domain/lead';
import { PlanT } from '@segurodeceso/core/domain/plans';
import { getStringDate } from '@segurodeceso/core/libs/dayjs';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import storage from './encryptedStorage';

const baseLead: Partial<LeadT> = {
  leadId: undefined,
  insurance: {
    productCode: '0',
    planId: '0',
    planCode: '',
    planName: '',
    noInsuredMembers: 1,
    coverages: [],
    sponsor: 'no-sponsor',
    prices: undefined,
  },
  personalInformation: {
    rut: '',
    name: '',
    surname: '',
    secondSurname: '',
    birthDate: '',
  },
  contactInformation: {
    email: {
      email: '',
      confirmEmail: '',
    },
    phone: '',
    region: undefined,
    address: '',
    commune: undefined,
    house: '',
  },
  checkout: {
    priceInCLP: {
      originalPrice: 0,
    },
    priceInUF: {
      originalPrice: 0,
    },
  },
  dependantsInformation: [],
  beneficiaries: [],
  originReferrer: undefined,
};

interface LeadStoreStateI {
  lead: Partial<LeadT>;
  isInitialized: boolean;
  setDefaultLead: () => void;
  setLeadId: (leadId: string) => void;
  setnoInsuredMembers: (noInsuredMembers: Pick<InsuranceT, 'noInsuredMembers'>) => void;
  setPlan: (
    insurance: Pick<InsuranceT, 'productCode' | 'planId' | 'planCode' | 'planName'>
  ) => void;
  setCheckout: (checkout: CheckoutT) => void;
  setPersonalInfo: (personalInformation: Partial<LeadT['personalInformation']>) => void;
  setContactInfo: (contactInformation: Partial<LeadT['contactInformation']>) => void;
  setDependantsInfo: (dependantsInformation: DependantsInformationT, index: number) => void;
  setBeneficiariesInfo: (beneficiaries: BeneficiaryT[]) => void;
  setLeadByPrefilledToken: (leadDB: Partial<LeadDbT>, plan: PlanT) => Promise<void>;
  resetDependantsInfo: () => void;
  resetBeneficiariesInfo: () => void;
  setIdentityValidation: (identityValidation: IdentityValidationT) => void;
  setReferral: (referral: string) => void;
  setOriginReferrer: (originReferrer: string) => void;
}

const useLeadStore = create<LeadStoreStateI>()(
  persist(
    (set, get) => ({
      lead: baseLead,
      isInitialized: false,
      setDefaultLead: () => set({ lead: baseLead }),
      setLeadId: (leadId) => set((state) => ({ lead: { ...state.lead, leadId } })),
      setnoInsuredMembers: (noInsuredMembers) =>
        set((state) => ({
          lead: { ...state.lead, insurance: { ...state.lead.insurance, ...noInsuredMembers } },
        })),
      setPlan: (insurance) =>
        set((state) => ({
          lead: { ...state.lead, insurance: { ...state.lead.insurance, ...insurance } },
        })),
      setCheckout: (checkout) => set((state) => ({ lead: { ...state.lead, checkout } })),
      setPersonalInfo: (personalInformation) =>
        set((state) => ({
          lead: {
            ...state.lead,
            personalInformation: { ...state.lead.personalInformation, ...personalInformation },
          },
        })),
      setContactInfo: (contactInformation) =>
        set((state) => ({
          lead: {
            ...state.lead,
            contactInformation: { ...state.lead.contactInformation, ...contactInformation },
          },
        })),
      setDependantsInfo: (dependantsInformation, index) =>
        set((state) => ({
          lead: {
            ...state.lead,
            dependantsInformation: [
              ...state.lead.dependantsInformation.slice(0, index),
              dependantsInformation,
            ],
          },
        })),
      setBeneficiariesInfo: (beneficiariesInfo) =>
        set((state) => ({
          lead: {
            ...state.lead,
            beneficiaries: beneficiariesInfo,
          },
        })),
      setIdentityValidation: (identityValidation: IdentityValidationT) =>
        set((state) => ({
          lead: {
            ...state.lead,
            identityValidation,
          },
        })),
      resetDependantsInfo: () =>
        set((state) => ({ lead: { ...state.lead, dependantsInformation: [] } })),
      resetBeneficiariesInfo: () =>
        set((state) => ({ lead: { ...state.lead, beneficiaries: [] } })),
      setLeadByPrefilledToken: async (leadDB: LeadDbT, plan: PlanT) => {      
        set(() => {
          const dependantsInformation = leadDB.dependantsInformation?.map((dependant) => {
            const relationshipAge = familyRelationships.find(
              (relation) => relation.value === dependant.relationshipId.toString()
            );
            return {
              rut: dependant.rut,
              name: dependant.name,
              surname: dependant.surname,
              birthDate: dependant.birthDate,
              secondSurname: dependant.secondSurname !== '-' ? dependant.secondSurname : '',
              relationship: dependant.relationship,
              relationshipId: dependant.relationshipId,
              relationshipAge: {
                ...relationshipAge,
                birthDate: dependant.birthDate || '',
                familyRelationship: relationshipAge || null,
              },
            };
          });

          const beneficiaries = leadDB?.beneficiaries?.map((beneficiary) => {
            return {
              rut: beneficiary.rut,
              name: beneficiary.name,
              surname: beneficiary.surname,
              secondSurname: beneficiary.secondSurname !== '-' ? beneficiary.secondSurname : '',
              rate: beneficiary.rate,
              phone: beneficiary.phone,
              email: beneficiary.email,
            };
          });

          const leadData: Partial<LeadT> = {
            ...leadDB,
            personalInformation: {
              ...leadDB.personalInformation,
              secondSurname:
                leadDB.personalInformation.secondSurname !== '-'
                  ? leadDB.personalInformation.secondSurname
                  : '',
              birthDate: getStringDate(new Date(leadDB.personalInformation.birthDate)),
            },
            dependantsInformation,
            contactInformation: {
              ...leadDB.contactInformation,
              phone: leadDB.contactInformation.phone.replace('+56', ''),
              email: {
                email: leadDB.contactInformation.email,
                confirmEmail: leadDB.contactInformation.email,
              },
              region: {
                label: leadDB.contactInformation.region,
                value: leadDB.contactInformation.regionId.toString(),
              },
              commune: {
                label: leadDB.contactInformation.commune,
                value: leadDB.contactInformation.communeId.toString(),
              },
            },
            insurance: {
              ...leadDB.insurance,
              coverages: plan?.coverages ?? [],
              prices: {
                price: plan.prices?.price,
                priceWithDiscount: plan.prices?.priceWithDiscount,
              },
              noInsuredMembers: leadDB?.dependantsInformation?.length
                ? leadDB?.dependantsInformation?.length + 1
                : 1,
            },
          };

          if (beneficiaries && beneficiaries.length > 0) {
            leadData.beneficiaries = beneficiaries;
          }

          return { lead: { ...leadData } };
        })},
      setReferral: (referral) => set((state) => ({ lead: { ...state.lead, referral } })),
      setOriginReferrer: (originReferrer) =>
        set((state) => ({ lead: { ...state.lead, originReferrer } })),
    }),
    {
      name: 'deceso-lead-storage',
      storage,
    }
  )
);

export default useLeadStore;
